import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Drawer } from '@prio365/prio365-react-library';
import { makePrioStyles } from '../../../theme/utils';
import { useTheme } from 'react-jss';
import { PrioTheme } from '../../../theme/types';
import Flex from '../../../components/Flex';
import HTMLTextEditor from '../../../components/HTMLTextEditor';
import {
  getIndividualAbsenceNoteDrawerOpen,
  getUserMe,
} from '../../../apps/main/rootReducer';
import { closeIndividualAbsenceNoteDrawer } from '../actions/individualAbsenceNoteDrawer';
import useIndividualAbsenceNote, {
  ABSENCE_CUSTOM_NOTE_SETTING_KEY,
} from '../hooks/useIndividualAbsenceNote';
import { CreateSetting, ESettingScope } from '../../../models/Settings';
import { apiDeleteSetting, apiSaveUserSetting, apiUpdateSetting } from '../../settings/api';
import { notification } from 'antd';

const REDUCED_TOOLBAR_BUTTONS = {
  moreText: {
    buttons: ['fontSize', 'bold', 'italic', 'textColor'],
    buttonsVisible: 4,
  },
  moreParagraph: {
    buttons: ['formatOL', 'formatUL'],
    buttonsVisible: 2,
  },
  moreRich: {
    buttons: ['insertLink'],
    buttonsVisible: 1,
  },
};

const useStyles = makePrioStyles((theme) => ({
  label: {
    fontSize: theme.font.fontSize.extraSmall,
    color: theme.colors.application.typography.muted,
    marginBottom: theme.spacing.small,
  },
  editorWrapper: {
    height: 200,
  },
  footer: {
    width: '100%',
  },
  deleteButton: {
    color: theme.colors.base.red.default,
  },
}));

export const IndividualAbsenceNoteDrawer: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme<PrioTheme>();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const open = useSelector(getIndividualAbsenceNoteDrawerOpen);
  const userMe = useSelector(getUserMe);
  const { absenceNote, defaultAbsenceNote, settingId } = useIndividualAbsenceNote();

  const [internalNote, setInternalNote] = useState('');
  const [externalNote, setExternalNote] = useState('');

  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setInternalNote(absenceNote.internalNote || defaultAbsenceNote.internalNote);
      setExternalNote(absenceNote.externalNote || defaultAbsenceNote.externalNote);
    }
    prevOpenRef.current = open;
  }, [open, absenceNote, defaultAbsenceNote]);

  const queryKey = useMemo(
    () => [
      ESettingScope.USER,
      userMe?.id,
      'setting',
      ABSENCE_CUSTOM_NOTE_SETTING_KEY,
    ],
    [userMe?.id]
  );

  const handleClose = useCallback(() => {
    dispatch(closeIndividualAbsenceNoteDrawer());
  }, [dispatch]);

  const handleSave = useCallback(async () => {
    if (!userMe?.id) return;

    const settingData: CreateSetting = {
      jsonValue: JSON.stringify({ internalNote, externalNote }),
      parentSettingId: '',
      relationId: '',
      isDefault: false,
      description: '',
    };

    const response = settingId
      ? await apiUpdateSetting(settingId, settingData)
      : await apiSaveUserSetting(
          userMe.id,
          ABSENCE_CUSTOM_NOTE_SETTING_KEY,
          settingData
        );
    if (!response.result.ok) {
      notification.error({ message: t('common:error') });
      return;
    }
    queryClient.invalidateQueries({ queryKey });
    handleClose();
  }, [
    internalNote,
    externalNote,
    userMe?.id,
    settingId,
    queryClient,
    queryKey,
    handleClose,
    t,
  ]);

  const handleDelete = useCallback(async () => {
    if (!settingId) return;
    const response = await apiDeleteSetting(settingId);
    if (!response.result.ok) {
      notification.error({ message: t('common:error') });
      return;
    }
    queryClient.invalidateQueries({ queryKey });
    handleClose();
  }, [settingId, queryClient, queryKey, handleClose, t]);

  const footer = (
    <Flex.Row
      className={classes.footer}
      justifyContent="space-between"
      alignItems="center"
    >
      <Flex.Item>
        {settingId && (
          <Button
            type="link"
            className={classes.deleteButton}
            onClick={handleDelete}
          >
            {t('absences:individualAbsenceNote.delete')}
          </Button>
        )}
      </Flex.Item>
      <Flex.Row childrenGap={theme.spacing.small}>
        <Button type="default" onClick={handleClose}>
          {t('absences:individualAbsenceNote.cancel')}
        </Button>
        <Button onClick={handleSave}>
          {t('absences:individualAbsenceNote.save')}
        </Button>
      </Flex.Row>
    </Flex.Row>
  );

  return (
    <Drawer
      title={t('absences:individualAbsenceNote.drawerTitle')}
      visible={open}
      onClose={handleClose}
      footer={footer}
      closable
    >
      <Flex.Column childrenGap={theme.spacing.regular}>
        <Flex.Column>
          <div className={classes.label}>
            {t('absences:individualAbsenceNote.internalNote')}
          </div>
          <div className={classes.editorWrapper}>
            <HTMLTextEditor
              value={internalNote}
              onChange={setInternalNote}
              toolbarButtons={REDUCED_TOOLBAR_BUTTONS}
            />
          </div>
        </Flex.Column>
        <Flex.Column>
          <div className={classes.label}>
            {t('absences:individualAbsenceNote.externalNote')}
          </div>
          <div className={classes.editorWrapper}>
            <HTMLTextEditor
              value={externalNote}
              onChange={setExternalNote}
              toolbarButtons={REDUCED_TOOLBAR_BUTTONS}
            />
          </div>
        </Flex.Column>
      </Flex.Column>
    </Drawer>
  );
};

export default IndividualAbsenceNoteDrawer;
